import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueue } from '../../hooks/useQueue'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { useAudioAlerts } from '../../hooks/useAudioAlerts'
import { QueueItem } from './QueueItem'
import { QueueFilters } from './QueueFilters'
import { apiPost, getErrorMessage } from '../../utils/api'
import { useStatusCounts } from '../../hooks/useStatusCounts'

export const QueueView: React.FC = () => {
  const navigate = useNavigate()
  const { currentAgent } = useAuth()
  const { addNotification } = useNotifications()
  const {
    chats,
    loading,
    error,
    refresh,
    filterTags,
    setFilterTags,
    filterStatus,
    setFilterStatus,
  } = useQueue()
  const { stopQueueAlert, isAlertingChat } = useAudioAlerts()
  const statusCounts = useStatusCounts()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [filterPriority, setFilterPriority] = useState('')
  const [pickingUp, setPickingUp] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0) // 0=hidden, 1=first warning, 2=final confirmation
  const [deleting, setDeleting] = useState(false)

  // Close modal on Escape key
  useEffect(() => {
    if (deleteStep === 0) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDeleteStep(0)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [deleteStep])

  const handleDeleteAll = useCallback(async () => {
    setDeleting(true)
    try {
      await apiPost('/chats/delete-all', {})
      addNotification('All conversations have been deleted', 'success')
      setDeleteStep(0)
      refresh()
    } catch (err) {
      addNotification(
        getErrorMessage(err, 'Failed to delete conversations'),
        'error'
      )
    } finally {
      setDeleting(false)
    }
  }, [addNotification, refresh])

  // ── Keep the selected tab (Queued/Open/Missed/…) in the URL ────────────────
  // Opening a chat re-parents QueueView inside QueueWithChat, which REMOUNTS it
  // and reset filterStatus back to 'queued' — so the list jumped off Missed. We
  // mirror the tab in ?status= so it survives the remount and can be linked to.
  const urlStatus = searchParams.get('status')
  useEffect(() => {
    if (urlStatus && urlStatus !== filterStatus) setFilterStatus(urlStatus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStatus])

  const handleStatusChange = (status: string) => {
    setFilterStatus(status)
    const next = new URLSearchParams(searchParams)
    if (status) next.set('status', status)
    else next.delete('status')
    setSearchParams(next, { replace: true })
  }

  const handleSelectChat = (chatId: string) => {
    setSelectedChatId(chatId)
    const qs = filterStatus ? `?status=${encodeURIComponent(filterStatus)}` : ''
    navigate(`/queue/${chatId}${qs}`)
  }

  const handlePickup = async (chatId: string) => {
    if (!currentAgent) return
    setPickingUp(chatId)
    try {
      await apiPost(`/chats/${chatId}/assign`, { agentId: currentAgent.id })
      stopQueueAlert(chatId) // Stop the looping alert for this specific chat
      addNotification('Chat assigned successfully', 'success')
      navigate(`/my-chats/${chatId}`)
    } catch (err) {
      addNotification(getErrorMessage(err, 'Failed to pickup chat'), 'error')
    } finally {
      setPickingUp(null)
    }
  }

  const handleRestore = async (chatId: string) => {
    try {
      await apiPost(`/chats/${chatId}/restore`, {})
      addNotification('Chat restored from archive', 'success')
      refresh()
    } catch (err) {
      addNotification(getErrorMessage(err, 'Failed to restore chat'), 'error')
    }
  }

  const filteredChats = chats.filter((chat) => {
    if (filterPriority && chat.priority !== filterPriority) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        chat.customerName?.toLowerCase().includes(q) ||
        chat.customerEmail?.toLowerCase().includes(q) ||
        chat.tags.some((t) => t.toLowerCase().includes(q))
      )
    }
    return true
  })

  return (
    <div className="h-full flex flex-col">
      {/* Header — the area title + tabs live in the Chats shell above; here we
          show just the Active tab's own controls: search, actions, status filters. */}
      <div className="px-6 pt-4 pb-4 bg-surface border-b border-border">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
            />
          </div>
          <button
            onClick={() => refresh()}
            className="p-2 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-border transition-colors shrink-0"
            title="Refresh"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin-slow' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
          </button>
          <button
            onClick={() => setDeleteStep(1)}
            className="p-2 rounded-lg text-txt-secondary hover:text-error hover:bg-error/5 border border-border transition-colors shrink-0"
            title="Delete all conversations"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <QueueFilters
          selectedStatus={filterStatus}
          onStatusChange={handleStatusChange}
          selectedTags={filterTags}
          onTagsChange={setFilterTags}
          selectedPriority={filterPriority}
          onPriorityChange={setFilterPriority}
          statusCounts={statusCounts}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-error-light border border-error/20 text-error text-sm">
          {error}
        </div>
      )}

      {/* Queue list */}
      <div className="flex-1 overflow-y-auto">
        {loading && filteredChats.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <div className="flex flex-col items-center gap-3">
              <svg className="w-8 h-8 text-brand-500 animate-spin-slow" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm text-txt-secondary">Loading conversations...</span>
            </div>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center px-6">
            <svg className="w-12 h-12 text-txt-tertiary mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-17.5 0V6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v6.75m-17.5 0v6a2.25 2.25 0 002.25 2.25h13.5a2.25 2.25 0 002.25-2.25v-6" />
            </svg>
            <p className="text-sm font-medium text-txt-secondary">No conversations found</p>
            <p className="text-xs text-txt-tertiary mt-1">
              {searchQuery ? 'Try adjusting your search or filters' : 'New conversations will appear here'}
            </p>
          </div>
        ) : (
          filteredChats.map((chat) => (
            <QueueItem
              key={chat.id}
              chat={chat}
              isSelected={selectedChatId === chat.id}
              isAlerting={isAlertingChat(chat.id)}
              onClick={() => handleSelectChat(chat.id)}
              onPickup={!chat.assignedAgentId ? () => handlePickup(chat.id) : undefined}
              isPickingUp={pickingUp === chat.id}
              onRestore={(chat.status as string) === 'archived' ? () => handleRestore(chat.id) : undefined}
            />
          ))
        )}
      </div>

      {/* ── Delete All — First Warning Modal ── */}
      {deleteStep === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteStep(0)} />
          <div className="relative bg-surface rounded-xl shadow-xl border border-border p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-warning/10">
                <svg className="w-6 h-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-txt-primary">Delete All Conversations</h2>
            </div>
            <p className="text-sm text-txt-secondary mb-6">
              This will permanently delete <strong>all conversations</strong> regardless of their status — including open, queued, and closed chats. All messages will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm font-medium text-txt-secondary hover:text-txt-primary bg-surface-secondary hover:bg-surface-hover rounded-lg border border-border transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setDeleteStep(2)}
                className="px-4 py-2 text-sm font-medium text-white bg-warning hover:bg-warning/90 rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete All — Final Confirmation Modal ── */}
      {deleteStep === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteStep(0)} />
          <div className="relative bg-surface rounded-xl shadow-xl border border-border p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-error/10">
                <svg className="w-6 h-6 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-error">Are you absolutely sure?</h2>
            </div>
            <p className="text-sm text-txt-secondary mb-2">
              This action <strong>cannot be undone</strong>. Every conversation and message will be permanently deleted from the system.
            </p>
            <p className="text-sm text-txt-secondary mb-6">
              {chats.length} conversation{chats.length !== 1 ? 's' : ''} will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteStep(0)}
                className="px-4 py-2 text-sm font-medium text-txt-secondary hover:text-txt-primary bg-surface-secondary hover:bg-surface-hover rounded-lg border border-border transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-error hover:bg-error/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete Everything'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
